import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Package,
  Globe,
  Layers,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";
import {
  releasesApi,
  servicesApi,
  serviceTemplatesApi,
  environmentsApi,
  ReleaseStepType,
} from "../api/client";
import { titleCase } from "../lib/utils";

const stepTypeLabels: Record<ReleaseStepType, string> = {
  container_deploy: "Container Deploy",
  liquibase: "Liquibase Migration",
  workflow_model: "Workflow Model",
  sql_script: "SQL Script",
  config_change: "Config Change",
  manual_task: "Manual Task",
  curl_command: "cURL Command",
  file_operation: "File Operation",
  service_restart: "Service Restart",
  pre_deployment: "Pre-Deployment",
  post_deployment: "Post-Deployment",
};

type WizardStep = "basics" | "environments" | "steps" | "artifacts" | "review";

interface ReleaseFormData {
  service_id: string;
  version: string;
  title: string;
  description: string;
  environment_ids: string[];
  use_template: boolean;
  template_id: string;
  steps: Array<{
    step_type: ReleaseStepType;
    title: string;
    description: string;
    is_automated: boolean;
    jenkins_job_name: string;
  }>;
  artifacts: Array<{
    artifact_name: string;
    artifact_type: string;
    artifact_version: string;
    artifact_url: string;
  }>;
}

export default function ReleaseCreate() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>("basics");
  const [formData, setFormData] = useState<ReleaseFormData>({
    service_id: "",
    version: "",
    title: "",
    description: "",
    environment_ids: [],
    use_template: false,
    template_id: "",
    steps: [],
    artifacts: [],
  });

  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const { data: environmentsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const { data: templatesData } = useQuery({
    queryKey: ["service-templates", formData.service_id],
    queryFn: () =>
      serviceTemplatesApi.list(
        formData.service_id ? { service_id: formData.service_id } : undefined,
      ),
    enabled: true,
  });

  const services = servicesData?.services || [];
  const environments = environmentsData?.environments || [];
  const templates = templatesData?.templates || [];

  const createReleaseMutation = useMutation({
    mutationFn: async () => {
      // Create the release
      const release = await releasesApi.create({
        service_id: formData.service_id,
        version: formData.version,
        title: formData.title,
        description: formData.description || undefined,
        environment_ids: formData.environment_ids,
      });

      // Add steps
      for (let i = 0; i < formData.steps.length; i++) {
        const step = formData.steps[i];
        await releasesApi.createStep(release.id, {
          step_type: step.step_type,
          display_order: i + 1,
          title: step.title,
          description: step.description || undefined,
          is_automated: step.is_automated,
          jenkins_job_name: step.jenkins_job_name || undefined,
        });
      }

      // Add artifacts
      for (const artifact of formData.artifacts) {
        await releasesApi.createArtifact(release.id, {
          artifact_name: artifact.artifact_name,
          artifact_type: artifact.artifact_type,
          artifact_version: artifact.artifact_version,
          artifact_url: artifact.artifact_url || undefined,
        });
      }

      return release;
    },
    onSuccess: (release) => {
      navigate(`/releases/${release.id}`);
    },
  });

  const wizardSteps: {
    id: WizardStep;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { id: "basics", label: "Basics", icon: <FileText className="w-5 h-5" /> },
    {
      id: "environments",
      label: "Environments",
      icon: <Globe className="w-5 h-5" />,
    },
    { id: "steps", label: "Steps", icon: <Layers className="w-5 h-5" /> },
    {
      id: "artifacts",
      label: "Artifacts",
      icon: <Package className="w-5 h-5" />,
    },
    { id: "review", label: "Review", icon: <Check className="w-5 h-5" /> },
  ];

  const currentStepIndex = wizardSteps.findIndex((s) => s.id === currentStep);

  const canProceed = () => {
    switch (currentStep) {
      case "basics":
        return formData.service_id && formData.version && formData.title;
      case "environments":
        return formData.environment_ids.length > 0;
      case "steps":
        return true; // Steps are optional
      case "artifacts":
        return true; // Artifacts are optional
      case "review":
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < wizardSteps.length) {
      setCurrentStep(wizardSteps[nextIndex].id);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(wizardSteps[prevIndex].id);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setFormData((prev) => ({
        ...prev,
        template_id: templateId,
        use_template: true,
        steps: template.steps.map((s) => ({
          step_type: s.step_type,
          title: s.title,
          description: s.description || "",
          is_automated: s.is_automated,
          jenkins_job_name: s.jenkins_job_name || "",
        })),
      }));
    }
  };

  const addStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          step_type: "manual_task",
          title: "",
          description: "",
          is_automated: false,
          jenkins_job_name: "",
        },
      ],
    }));
  };

  const removeStep = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const updateStep = (index: number, field: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step,
      ),
    }));
  };

  const addArtifact = () => {
    setFormData((prev) => ({
      ...prev,
      artifacts: [
        ...prev.artifacts,
        {
          artifact_name: "",
          artifact_type: "container",
          artifact_version: "",
          artifact_url: "",
        },
      ],
    }));
  };

  const removeArtifact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      artifacts: prev.artifacts.filter((_, i) => i !== index),
    }));
  };

  const updateArtifact = (index: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      artifacts: prev.artifacts.map((artifact, i) =>
        i === index ? { ...artifact, [field]: value } : artifact,
      ),
    }));
  };

  const toggleEnvironment = (envId: string) => {
    setFormData((prev) => ({
      ...prev,
      environment_ids: prev.environment_ids.includes(envId)
        ? prev.environment_ids.filter((id) => id !== envId)
        : [...prev.environment_ids, envId],
    }));
  };

  const selectedService = services.find((s) => s.id === formData.service_id);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/releases")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Releases
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create New Release</h1>
        <p className="text-gray-600 mt-1">
          Create a structured release with deployment steps and artifacts
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {wizardSteps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => {
                  if (index <= currentStepIndex) {
                    setCurrentStep(step.id);
                  }
                }}
                disabled={index > currentStepIndex}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  step.id === currentStep
                    ? "bg-brand-600 text-white"
                    : index < currentStepIndex
                      ? "bg-brand-100 text-brand-700 hover:bg-brand-200"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                {step.icon}
                <span className="font-medium">{step.label}</span>
              </button>
              {index < wizardSteps.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-2 ${
                    index < currentStepIndex ? "bg-brand-600" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        {currentStep === "basics" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Basic Information
            </h2>

            <div>
              <label className="label">Service *</label>
              <select
                value={formData.service_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    service_id: e.target.value,
                    template_id: "",
                    use_template: false,
                  }))
                }
                className="input w-full"
                required
              >
                <option value="">Select a service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {titleCase(service.name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Version *</label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      version: e.target.value,
                    }))
                  }
                  className="input w-full"
                  placeholder="e.g., 1.2.3 or 2024.01.15"
                  required
                />
              </div>
              <div>
                <label className="label">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="input w-full"
                  placeholder="e.g., January 2024 Release"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input w-full"
                rows={3}
                placeholder="Optional description of this release..."
              />
            </div>

            {templates.length > 0 && (
              <div>
                <label className="label">Use Template (Optional)</label>
                <select
                  value={formData.template_id}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="input w-full"
                >
                  <option value="">Start from scratch</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                      {template.is_default && " (Default)"}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Templates pre-populate deployment steps
                </p>
              </div>
            )}
          </div>
        )}

        {currentStep === "environments" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Target Environments
            </h2>
            <p className="text-gray-600">
              Select the environments this release will be deployed to
            </p>

            <div className="grid grid-cols-2 gap-4">
              {environments.map((env) => (
                <label
                  key={env.id}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    formData.environment_ids.includes(env.id)
                      ? "border-brand-600 bg-brand-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.environment_ids.includes(env.id)}
                    onChange={() => toggleEnvironment(env.id)}
                    className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{env.name}</div>
                    <div className="text-sm text-gray-500">{env.slug}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {currentStep === "steps" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Deployment Steps
                </h2>
                <p className="text-gray-600">
                  Define the steps to execute during deployment
                </p>
              </div>
              <button onClick={addStep} className="btn btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Step
              </button>
            </div>

            {formData.steps.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No steps defined yet</p>
                <button onClick={addStep} className="btn btn-secondary mt-4">
                  Add First Step
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.steps.map((step, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-gray-400 mt-2">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="label">Step Type</label>
                            <select
                              value={step.step_type}
                              onChange={(e) =>
                                updateStep(index, "step_type", e.target.value)
                              }
                              className="input w-full"
                            >
                              {Object.entries(stepTypeLabels).map(
                                ([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>
                          <div>
                            <label className="label">Title</label>
                            <input
                              type="text"
                              value={step.title}
                              onChange={(e) =>
                                updateStep(index, "title", e.target.value)
                              }
                              className="input w-full"
                              placeholder="Step title"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">Description</label>
                          <textarea
                            value={step.description}
                            onChange={(e) =>
                              updateStep(index, "description", e.target.value)
                            }
                            className="input w-full"
                            rows={2}
                            placeholder="Optional description"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={step.is_automated}
                                onChange={(e) =>
                                  updateStep(
                                    index,
                                    "is_automated",
                                    e.target.checked,
                                  )
                                }
                                className="rounded border-gray-300"
                              />
                              <span className="text-sm text-gray-700">
                                Automated (Jenkins)
                              </span>
                            </label>
                          </div>
                          {step.is_automated && (
                            <div>
                              <input
                                type="text"
                                value={step.jenkins_job_name}
                                onChange={(e) =>
                                  updateStep(
                                    index,
                                    "jenkins_job_name",
                                    e.target.value,
                                  )
                                }
                                className="input w-full"
                                placeholder="Jenkins job name"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeStep(index)}
                        className="text-gray-400 hover:text-red-600 mt-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentStep === "artifacts" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Release Artifacts
                </h2>
                <p className="text-gray-600">
                  Components and versions included in this release
                </p>
              </div>
              <button onClick={addArtifact} className="btn btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Artifact
              </button>
            </div>

            {formData.artifacts.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No artifacts defined yet</p>
                <button
                  onClick={addArtifact}
                  className="btn btn-secondary mt-4"
                >
                  Add First Artifact
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.artifacts.map((artifact, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="label">Name</label>
                            <input
                              type="text"
                              value={artifact.artifact_name}
                              onChange={(e) =>
                                updateArtifact(
                                  index,
                                  "artifact_name",
                                  e.target.value,
                                )
                              }
                              className="input w-full"
                              placeholder="e.g., my-service"
                            />
                          </div>
                          <div>
                            <label className="label">Type</label>
                            <select
                              value={artifact.artifact_type}
                              onChange={(e) =>
                                updateArtifact(
                                  index,
                                  "artifact_type",
                                  e.target.value,
                                )
                              }
                              className="input w-full"
                            >
                              <option value="container">Container</option>
                              <option value="library">Library</option>
                              <option value="config">Config</option>
                              <option value="database">Database</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="label">Version</label>
                            <input
                              type="text"
                              value={artifact.artifact_version}
                              onChange={(e) =>
                                updateArtifact(
                                  index,
                                  "artifact_version",
                                  e.target.value,
                                )
                              }
                              className="input w-full"
                              placeholder="e.g., 1.2.3"
                            />
                          </div>
                          <div>
                            <label className="label">URL (optional)</label>
                            <input
                              type="text"
                              value={artifact.artifact_url}
                              onChange={(e) =>
                                updateArtifact(
                                  index,
                                  "artifact_url",
                                  e.target.value,
                                )
                              }
                              className="input w-full"
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeArtifact(index)}
                        className="text-gray-400 hover:text-red-600 mt-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {currentStep === "review" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Review Release
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Basic Information</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Service</dt>
                    <dd className="text-gray-900">{selectedService?.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Version</dt>
                    <dd className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                      {formData.version}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Title</dt>
                    <dd className="text-gray-900">{formData.title}</dd>
                  </div>
                </dl>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Environments</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.environment_ids.map((envId) => {
                    const env = environments.find((e) => e.id === envId);
                    return (
                      <span
                        key={envId}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {env?.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">
                Steps ({formData.steps.length})
              </h3>
              {formData.steps.length === 0 ? (
                <p className="text-gray-500 text-sm">No steps defined</p>
              ) : (
                <div className="space-y-2">
                  {formData.steps.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 text-sm"
                    >
                      <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
                        {index + 1}
                      </span>
                      <span className="text-gray-900">{step.title}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {stepTypeLabels[step.step_type]}
                      </span>
                      {step.is_automated && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                          Automated
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">
                Artifacts ({formData.artifacts.length})
              </h3>
              {formData.artifacts.length === 0 ? (
                <p className="text-gray-500 text-sm">No artifacts defined</p>
              ) : (
                <div className="space-y-2">
                  {formData.artifacts.map((artifact, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 text-sm"
                    >
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900">
                        {artifact.artifact_name}
                      </span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                        {artifact.artifact_version}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({artifact.artifact_type})
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentStepIndex === 0}
          className="btn btn-secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {currentStep === "review" ? (
          <button
            onClick={() => createReleaseMutation.mutate()}
            disabled={createReleaseMutation.isPending}
            className="btn btn-primary"
          >
            {createReleaseMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Create Release
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="btn btn-primary"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>
    </div>
  );
}
